import {
  PROVIDER_NAME,
  PROVIDER_POWERED_BY,
  DEFAULT_ACCENT,
  WORDMARK_SRC,
  WORDMARK_W,
  WORDMARK_H,
} from "./identity";
import "./provider.css";

type Props = {
  /**
   * The game this badge is sitting on top of. Only used to key the accent
   * stylesheet below — a badge with no game (the hub, the 404) omits it.
   */
  gameId?: string;
  /**
   * The game's accent. It no longer tints the mark (the artwork carries its own
   * colour) — it is still passed so the emitted stylesheet keeps one accent
   * token per game for anything nearby that wants it.
   */
  accent?: string | undefined;
};

/**
 * "Powered by" plus the studio lockup, on every game screen at every size.
 *
 * WHY IT IS AN `<img>` AND NOT A MASK. It used to be a `mask-image` stencil
 * poured full of the host game's accent, which was the right call while the mark
 * was a single white silhouette. The mark is now a colour lockup — a crimson pad
 * against white type — and a stencil throws all of that away, flattening the pad,
 * the type and the signature into one flat accent-coloured blob. The artwork is
 * drawn as artwork; the game's accent identifies the game elsewhere.
 *
 * WHY IT IS INERT. It sits over live playfields with drag targets and bet
 * buttons under it. It is `pointer-events: none` end to end and links nowhere;
 * a credit that can eat a cash-out click is worse than no credit. The mark
 * carries `role="img"` and the studio name, so it is still announced.
 *
 * WHERE IT SITS. Bottom right on a desktop window, bottom CENTRE below 1024px —
 * on a phone the right-hand corner is where the bet dock, the cash-out button
 * and the home-indicator inset all live. It is never hidden at any size; the
 * games reserve room for it instead (`--provider-badge-space` in provider.css).
 */
export default function ProviderBadge({ gameId, accent }: Props) {
  const key = gameId ?? "hub";
  const color = accent ?? DEFAULT_ACCENT;

  return (
    <>
      {/* Emitted as a real stylesheet keyed by data-game rather than as an
          inline style — the hub does the same thing for its card glows, because
          inline styles are what Dark Reader rewrites and hydration then trips
          over. */}
      <style>{`.provider-badge[data-game="${key}"]{--provider-accent:${color}}`}</style>
      <div className="provider-badge" data-game={key}>
        <span className="provider-badge__label">{PROVIDER_POWERED_BY}</span>
        <img
          className="provider-badge__mark"
          src={WORDMARK_SRC}
          alt={PROVIDER_NAME}
          width={WORDMARK_W}
          height={WORDMARK_H}
          draggable={false}
        />
      </div>
    </>
  );
}
